// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { isFieldForCategory } from '../../util/fieldHelpers';
import { isDoorstepVerifiedListing } from '../../util/listing';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  ListingCardThumbnail,
  ListingVerifiedBadge,
  NamedLink,
  ResponsiveImage,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const ITEM_CONDITION_FIELD_KEY = 'ItemCondition';

const findEnumOptionLabel = (listingFields = [], fieldKey, selectedValue) => {
  if (!selectedValue) {
    return null;
  }

  const fieldConfig = listingFields.find(field => field.key === fieldKey);
  const normalizedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
  const matchedOption = fieldConfig?.enumOptions?.find(option =>
    normalizedValues.includes(option.option)
  );

  return matchedOption?.label || normalizedValues[0];
};

const findCategoryLabel = (categories = [], selectedValue) => {
  if (!selectedValue) {
    return null;
  }

  for (const category of categories) {
    if (category.id === selectedValue) {
      return category.name;
    }

    const nestedMatch = findCategoryLabel(category.subcategories || [], selectedValue);
    if (nestedMatch) {
      return nestedMatch;
    }
  }

  return selectedValue;
};

const formatMetadataLabel = value => {
  return typeof value === 'string' ? value.replace(/['’]/g, '').trim() : value;
};

const isSizeField = fieldConfig => {
  if (!['enum', 'multi-enum'].includes(fieldConfig?.schemaType)) {
    return false;
  }

  const labelsToCheck = [
    fieldConfig?.key,
    fieldConfig?.label,
    fieldConfig?.showConfig?.label,
    fieldConfig?.saveConfig?.label,
  ]
    .filter(Boolean)
    .join(' ');

  return /size/i.test(labelsToCheck);
};

const findListingSizeLabel = (listingFields = [], publicData = {}) => {
  const selectedCategoryIds = [
    publicData?.categoryLevel3,
    publicData?.categoryLevel2,
    publicData?.categoryLevel1,
  ].filter(Boolean);

  for (const categoryId of selectedCategoryIds) {
    const matchingField = listingFields.find(fieldConfig => {
      if (!isSizeField(fieldConfig)) {
        return false;
      }

      const selectedValue = publicData?.[fieldConfig.key];
      return Boolean(selectedValue) && isFieldForCategory([categoryId], fieldConfig);
    });

    if (matchingField) {
      return findEnumOptionLabel(listingFields, matchingField.key, publicData?.[matchingField.key]);
    }
  }

  return null;
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyImage : ResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {boolean?} props.showSearchMetadata
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showSearchMetadata = false,
    showVerifiedTooltip = false,
    lazyLoadImage = true,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData } = listing?.attributes || {};
  const slug = createSlug(title);
  const isVerifiedListing = isDoorstepVerifiedListing(listing);
  const verifiedTooltip = showVerifiedTooltip
    ? intl.formatMessage({ id: 'ListingVerifiedBadge.tooltip' })
    : null;
  const verifiedLabel = intl.formatMessage({ id: 'ListingVerifiedBadge.iconLabel' });

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const listingFields = config?.listing?.listingFields || [];
  const categoryTree =
    config?.categoryConfiguration?.categories || config?.categories?.categories || [];

  const itemCondition = formatMetadataLabel(
    findEnumOptionLabel(
      listingFields,
      ITEM_CONDITION_FIELD_KEY,
      publicData?.[ITEM_CONDITION_FIELD_KEY]
    )
  );
  const selectedCategoryId = publicData?.categoryLevel1;
  const categoryLabel = formatMetadataLabel(findCategoryLabel(categoryTree, selectedCategoryId));
  const sizeLabel = formatMetadataLabel(findListingSizeLabel(listingFields, publicData));
  const metadataItems = [itemCondition, categoryLabel, sizeLabel].filter(Boolean);

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          isVerified={isVerifiedListing}
          verifiedLabel={verifiedLabel}
          tooltipText={verifiedTooltip}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        {showPrice ? (
          <div className={css.priceRow}>
            <div className={css.price} title={priceTooltip}>
              {priceMessage}
            </div>
            {isVerifiedListing ? (
              <ListingVerifiedBadge
                className={css.priceVerifiedBadge}
                label={verifiedLabel}
                tooltipText={verifiedTooltip}
              />
            ) : null}
          </div>
        ) : null}
        <div className={css.mainInfo}>
          {showListingImage && (
            <div className={classNames(css.title, { [css.lightText]: darkMode })}>
              {titleFormatted}
            </div>
          )}
          {showSearchMetadata ? (
            <div className={css.metadataRow}>
              {metadataItems.map(item => (
                <span key={item} className={css.metadataItem}>
                  {item}
                </span>
              ))}
            </div>
          ) : null}
          {showAuthorInfo ? (
            <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
              {authorName}
            </div>
          ) : null}
        </div>
      </div>
    </NamedLink>
  );
};

export default ListingCard;
